import axios, {AxiosRequestConfig} from "axios";
import {AuditData} from "@/api/auth";
import {ContactRelationResult, ContactRelationType,} from "@/api/contactRelation";
import qs from 'qs';

export enum PhoneNumberType {
    LANDLINE = "LANDLINE",
    MOBILE = "MOBILE",
    FAX = "FAX",
    BUSINESS = "BUSINESS",
}

export enum Salutation {
    MR = "MR",
    MS = "MS",
}

export enum AddressType {
    BUSINESS = "BUSINESS",
    HOME = "HOME",
    COTTAGE = "COTTAGE",
}

export interface PhoneNumber {
    id: string | null;
    number: string;
    type: PhoneNumberType;
    sharedByContactPersonRef?: string | null;
    version: number | null;
}

export interface EmailAddress {
    id: string | null;
    address: string;
    favorite: boolean;
    sharedByContactPersonRef?: string | null;
    version: number | null;
}

export interface Address {
    id: string | null;
    street: string | null;
    additionalAddress: string | null;
    zipCode: string | null;
    country: string | null;
    state: string | null;
    city: string | null;
    addressType: AddressType;
    shareable: boolean;
    contactRef: string | null;
    alternativeGP: string | null;
    sharedByContactPersonRef?: string | null;
    version: number | null;
}

export enum DocumentType {
    PDF = "PDF",
    IMAGE = "IMAGE",
    WORD = "WORD",
    EMAIl = "EMAIL",
}

export enum LegalType {
    NATURAL = "NATURAL",
    LEGAL = "LEGAL",
}

export enum CustomerType {
    PRIVATE = "PRIVATE",
    BUSINESS = "BUSINESS",
    RESELLER_25 = "RESELLER_25",
    RESELLER_10 = "RESELLER_10",
    SUPPLIER = "SUPPLIER",
    FIRE_HELPER = "FIRE_HELPER"
}

export interface Attachment {
    id: string | null;
    name: string | null;
    documentType: DocumentType;
}

export interface ToDos {
    id: string | null;
    name: string | null;
}

export interface NumberCheck {
    value: string | null;
    verifiedBy: string | null;
    verifiedAt: Date | null;
    verified: boolean;
}

export interface Product {
    productName: string | null;
    quantity: number | null;
    version: number | null;
}

export interface ContactPerson {
    id: string | null;
    salutation?: Salutation;
    givenName: string | null;
    surname: string | null;
    birthdate?: Date | null;
    department?: string | null;
    version: number | null;
}

export interface Contact {
    id: string | null;
    legalForm?: LegalType;
    salutation: Salutation;
    title: string | null;
    givenName: string | null;
    surname: string | null;
    phoneNumbers: PhoneNumber[];
    emailAddresses: EmailAddress[];
    addresses: Address[];
    personalNotes: string | null;
    attachments: Attachment[];
    todos: ToDos[];
    companyName: string | null;
    department: string | null;
    birthdate: Date | null;
    customer: boolean;
    purchasedProducts: Product[];
    importId: string | null;
    website: string | null;
    sector: string | null;
    customerType: CustomerType | null;
    eoriCheck: NumberCheck | null;
    uidCheck: NumberCheck | null;
    resellerContactRef: string | null;
    notesReasonForFeuerring: string | null;
    latestFeuerringPurchase: Date | null;
    contentMailingsDdj: boolean;
    contentMailingsSeedsMagazine: boolean;
    contentMailingsPostcard: boolean;
    contentMailingsFreetext: string | null;
    placeOfExperience: boolean;
    auditData: AuditData;
    contactPersons: ContactPerson[];
    externalContactPersons: ContactPerson[];
    fullName?: string | null;
    version: number | null;
}

export interface ContactSearchResult {
    totalPages: number;
    totalElements: number;
    page: number;
    pageSize: number;
    contacts: ContactSearchRow[];
}

export interface ContactSearchRow {
    id: string | null;
    salutation: Salutation;
    title: string | null;
    givenName: string | null;
    surname: string | null;
    companyName: string | null;
    personalNotes: string | null;
    customer: boolean;
    addresses: Address[];
    emailAddresses: EmailAddress[] | null;
    phoneNumbers: PhoneNumber[];
    countContactHistories: string | null;
    eoriCheck: NumberCheck | null;
    uidCheck: NumberCheck | null;
    customerType: CustomerType | null;
    contactPersons: ContactPerson[];
    externalContactPersons: ContactPerson[];
    fullName?: string | null;
}

export interface ContactSearchRequest {
    givenName: string | null;
    surname: string | null;
    street: string | null;
    zipCode: string | null;
    city: string | null;
    country: string | null;
    phoneNumber: string | null;
    emailAddress: string | null;
    company: string | null;
    sector: string | null;
    customerType: CustomerType | null;
    placeOfExperience: boolean;
    customer: boolean;
    latestFeuerringPurchaseFrom?: Date | null;
    latestFeuerringPurchaseUntil?: Date | null;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortDesc?: boolean;
}

export function createContactInfos(sharedByContactPersonRef: string | null, contact: Contact | ContactSearchRow): string {
    if (sharedByContactPersonRef) {
        const contactPersons= [];
        contactPersons.push(...contact.contactPersons);
        if( contact.externalContactPersons) {
            contactPersons.push(...contact.externalContactPersons)
        }
        const found = contactPersons.find(value => value.id === sharedByContactPersonRef);
        if( found) {
            return found?.givenName + " " + found?.surname;
        }
    }
    return contact.fullName ?? "";
}

export function buildContactFullName(
    contact: Contact | ContactSearchRow,
    contactRelation: ContactRelationResult[] | null
) {
    let fullName = contact.fullName
    const relationInfos = buildRelationInfos(contactRelation);
    if (relationInfos !== null) {
        fullName = fullName + " (" + relationInfos + ")";
    }

    return fullName;
}


export function buildRelationInfos(
    contactRelation: ContactRelationResult[] | null
): string | null {
    if (contactRelation && contactRelation.length > 0) {
        let relationInfo = "";
        contactRelation
            .filter((cr) => cr.relationType === ContactRelationType.COUPLE)
            .forEach((relation) => {
                relationInfo += ",Paar - " + relation.withContact.fullName;
            });
        contactRelation
            .filter((cr) => cr.relationType === ContactRelationType.CHILD)
            .forEach((relation) => {
                relationInfo += ",Kind - " + relation.withContact.fullName;
            });
        contactRelation
            .filter((cr) => cr.relationType === ContactRelationType.PARENTS)
            .forEach((relation) => {
                relationInfo += ",Eltern - " + relation.withContact.fullName;
            });
        contactRelation
            .filter((cr) => cr.relationType === ContactRelationType.EMPLOYER)
            .forEach((relation) => {
                relationInfo +=
                    ",Arbeitgeber - " + relation.withContact.fullName;
            });
        contactRelation
            .filter((cr) => cr.relationType === ContactRelationType.RESELLER)
            .forEach((relation) => {
                relationInfo +=
                    ",Wiederverkäufer - " + relation.withContact.fullName;
            });
        if (relationInfo.length > 0) {
            return relationInfo.substr(1, relationInfo.length).replaceAll(",", ", ")
        }
    }
    return null;
}

interface ContactApi {
    search(
        contactSearchRequest: ContactSearchRequest
    ): Promise<ContactSearchResult>;

    searchByIds(ids: string[]): Promise<ContactSearchResult>;

    getById(id: string | null): Promise<Contact>;

    add(
        contact: Contact,
        blackBoardRef: string | null,
        emailRef: string | null
    ): Promise<string>;

    edit(id: string, contact: Contact): Promise<Contact>;

    delete(id: string): Promise<void>;

    listDuplicates(): Promise<ContactSearchResult>;

    addNewAddress(contactId: string, address: Address): Promise<string>;

    editAddress(contactId: string, id: string, address: Address): Promise<Address>;

    deleteAddress(contactId: string, id: string): Promise<void>;

    getAddressById(contactId: string, id: string): Promise<Address>;

    getPhoneNumberById(contactId: string, id: string): Promise<PhoneNumber>;

    addNewPhonenumber(
        contactId: string,
        phoneNumber: PhoneNumber
    ): Promise<string>;

    editPhonenumber(
        contactId: string,
        id: string,
        phoneNumber: PhoneNumber
    ): Promise<PhoneNumber>;

    deletePhonenumber(contactId: string, id: string): Promise<void>;

    calculateLatestFrPurchase(contactId: string): Promise<Date>;

    export(contactSearchRequest: ContactSearchRequest): Promise<void>;

    addEmail(contactId: string, email: EmailAddress): Promise<string>;

    deleteEmail(contactId: string, id: string): Promise<void>;

    editEmail(contactId: string, id: string, email: EmailAddress): Promise<EmailAddress>;

    getEmailById(contactId: string, id: string): Promise<EmailAddress>;

    listEmails(): Promise<string[]>;

    whichRecipientIsSupplierOrReseller(recipients: string[]): Promise<string[]>;
}

export const contactApi: ContactApi = {
    async search(contactSearchRequest) {
        return (await axios.post("/api/contacts/_search", contactSearchRequest))
            .data;
    },

    async searchByIds(ids) {
        return (await axios.post("/api/contacts/_byIds", ids)).data;
    },

    async edit(id, contact) {
        const result =  (await axios.put(`/api/contacts/${id}`, contact)).data;
        return {
            ...result,
            birthdate: result.birthdate ? new Date(result.birthdate) : null,
            uidCheck: result.uidCheck === null ? {} : result.uidCheck,
            eoriCheck: result.eoriCheck === null ? {} : result.eoriCheck,
            latestFeuerringPurchase: result.latestFeuerringPurchase
                ? new Date(result.latestFeuerringPurchase)
                : null,
        };
    },

    async getById(id) {
        if (id === null || id === undefined) {
            return null;
        }
        const result = (await axios.get(`/api/contacts/${id}`)).data;
        return {
            ...result,
            birthdate: result.birthdate ? new Date(result.birthdate) : null,
            uidCheck: result.uidCheck === null ? {} : result.uidCheck,
            eoriCheck: result.eoriCheck === null ? {} : result.eoriCheck,
            latestFeuerringPurchase: result.latestFeuerringPurchase
                ? new Date(result.latestFeuerringPurchase)
                : null,
        };
    },

    async add(contact, blackboardRef, emailRef) {
        blackboardRef = blackboardRef?.length == 0 ? null : blackboardRef;
        return (
            await axios.post(
                `/api/contacts`,
                contact,
                {
                    params: {
                        blackboardRef,
                        emailRef
                    }
                }
            )
        ).data;
    },

    async delete(id) {
        return await axios.delete(`/api/contacts/${id}`);
    },

    async listDuplicates() {
        return (await axios.get(`/api/contacts/duplicates`)).data;
    },

    async addNewAddress(contactId, address) {
        return (await axios.post(`/api/contacts/${contactId}/addresses`, address))
            .data;
    },

    async editAddress(contactId, id, address) {
        return (
            await axios.put(`/api/contacts/${contactId}/addresses/${id}`, address)
        ).data;
    },

    async deleteAddress(contactId, id) {
        return (await axios.delete(`/api/contacts/${contactId}/addresses/${id}`))
            .data;
    },

    async getAddressById(contactId, id) {
        return (await axios.get(`/api/contacts/${contactId}/addresses/${id}`)).data;
    },

    async getPhoneNumberById(contactId, id) {
        return (await axios.get(`/api/contacts/${contactId}/phone-numbers/${id}`))
            .data;
    },

    async addNewPhonenumber(contactId, phonenumber) {
        return (
            await axios.post(`/api/contacts/${contactId}/phone-numbers`, phonenumber)
        ).data;
    },

    async editPhonenumber(contactId, id, phonenumber) {
        return (
            await axios.put(
                `/api/contacts/${contactId}/phone-numbers/${id}`,
                phonenumber
            )
        ).data;
    },

    async deletePhonenumber(contactId, id) {
        return (
            await axios.delete(`/api/contacts/${contactId}/phone-numbers/${id}`)
        ).data;
    },

    async addEmail(contactId: string, email: EmailAddress): Promise<string> {
        return (
            await axios.post(`/api/contacts/${contactId}/emails`, email)
        ).data;
    },

    async editEmail(contactId: string, id: string, email: EmailAddress) {
        return (
            await axios.put(`/api/contacts/${contactId}/emails/${id}`, email)
        ).data;
    },

    async deleteEmail(contactId, id) {
        return (
            await axios.delete(`/api/contacts/${contactId}/emails/${id}`)
        ).data;
    },
    async getEmailById(contactId, id) {
        return (await axios.get(`/api/contacts/${contactId}/emails/${id}`))
            .data;
    },
    async calculateLatestFrPurchase(contactId) {
        const result = (
            await axios.get(`/api/contacts/${contactId}/calculate-latest-fr-purchase`)
        ).data;
        if (result) {
            return new Date(result);
        }
        return result;
    },

    async export(searchRequest) {
        const config = {
            responseType: "blob",
        } as AxiosRequestConfig;
        return await axios
            .post("/api/contacts/_exportAsResult", searchRequest, config)
            .then((response) => {
                const contentDisposition = response.headers[
                    "content-disposition"
                    ] as string;
                const fileName = extractFileName(contentDisposition);
                const contentType = response.headers["content-type"] as string;
                downloadFileViaBrowserAction(fileName, contentType, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    alert(
                        "An error occurred. Please try again later " + error.response.data
                    );
                }
            });
    },

    async listEmails() {
        return (await axios.get(`/api/contacts/emails`))
            .data;
    },
    async whichRecipientIsSupplierOrReseller(recipients) {
        return (await axios.get(`/api/contacts/emails/whichIsSupplierOrReseller`, {
            params: {
                recipients: recipients
            },
            paramsSerializer: (params) => {
                return qs.stringify(params, {arrayFormat: 'repeat'})
            },
        }))
            .data;
    },
};

function extractFileName(contentDisposition: string) {
    const startFileNameIndex = contentDisposition.indexOf("=") + 1;
    return contentDisposition.substring(startFileNameIndex);
}

function downloadFileViaBrowserAction(
    fileName: string,
    contentType: string,
    data: any
) {
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(
        new Blob([data], {type: contentType})
    );
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.onclick = (_e) => {
        window.addEventListener(
            "focus",
            (_e) => {
                URL.revokeObjectURL(link.href);
            },
            {once: true}
        );
    };
    link.click();
}
